import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDocumentTitle } from "../../../../../utils";
import { useOppBotContext, useOppBotMutations, useOppBotQuery } from "../hooks/useOppBotApi";
import { Avatar, ConfidenceBar, ErrorBox, ExternalLink, IntentPill, KeyValueList, ModePill, Panel, RecordTypePill, Spinner, StatusPill } from "../components/Ui";
import LinkedInProfileCard from "../components/LinkedInProfileCard";
import CloseOutPanel from "../components/CloseOutPanel";
import ThreadMessage from "../components/ThreadMessage";
import ActionTimeline, { describeValue } from "../components/ActionTimeline";
import ReviewForm from "../components/ReviewForm";
import { FLAG_LABELS, curateLabel, formatAmount, formatDateTime, formatMs, isProcessing, recordTypeMeta } from "../utils";

export default function EmailDetailPage() {
  const { id } = useParams();
  const { meta } = useOppBotContext();
  const params = useMemo(() => ({}), []);
  const query = useOppBotQuery(`emails/${id}`, params, { pollMs: 0 });
  const doc = query.data;
  const processing = doc ? isProcessing(doc.status) : false;
  // poll while the pipeline is running
  const live = useOppBotQuery(`emails/${id}`, params, { enabled: processing, pollMs: 4000 });
  const current = live.data && processing ? live.data : doc;
  useDocumentTitle(current && current.email ? `${current.email.subject || "Email"} · Opp Bot` : "Opp Bot");

  if (query.error) return <ErrorBox error={query.error} onRetry={query.reload} />;
  if (!current) return <Spinner label="Loading email…" />;
  return <Detail doc={current} meta={meta} refetch={() => { query.refetch(); live.refetch(); }} />;
}

function Detail({ doc, meta, refetch }) {
  const { reprocess, rawUrl } = useOppBotMutations();
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [showRaw, setShowRaw] = useState(false);
  const ai = doc.ai || {};
  const parsed = doc.parsed || {};
  const result = doc.result || {};
  const plan = doc.plan || {};
  const processing = isProcessing(doc.status);
  const threshold = meta ? meta.autoApplyMinConfidence : undefined;
  const isRequest = parsed.mode === "request";
  const isClose = ai.intent === "close_opportunity";
  const closeOut = ai.closeOut || null;
  const contextOpps = (doc.context && doc.context.openOpportunities) || [];
  const closeTarget = isClose ? contextOpps.find((o) => o.sfId === ai.existingOpportunityId) || null : null;
  const linkedInProfiles = (doc.context && doc.context.linkedInProfiles) || [];
  const forwarderName = (doc.forwarder && doc.forwarder.name) || "unknown";

  const onReprocess = async () => {
    setBusy("reprocess");
    setErr("");
    try {
      await reprocess(doc._id);
      refetch();
    } catch (e) {
      setErr(e.message || "Reprocess failed");
    } finally {
      setBusy("");
    }
  };

  const oppFields = (() => {
    const o = ai.opportunity || {};
    const isCurate = /curate/i.test(o.recordTypeName || "");
    return [
      { key: "Name", value: o.name },
      { key: "Business line", value: o.recordTypeName ? <RecordTypePill name={o.recordTypeName} curateCategory={o.curateCategory} /> : "" },
      { key: "Stage", value: o.stageName },
      ...(isCurate
        ? [
            { key: "Curate category", value: o.curateCategory ? curateLabel(o.curateCategory) : "" },
            { key: "P&L type", value: o.plType },
          ]
        : [{ key: "Summit event", value: o.summitEventName }]),
      { key: "Type", value: o.type },
      { key: "Lead source", value: o.leadSource },
      { key: "Owner", value: o.ownerEmail },
      { key: "Amount", value: o.amount !== null && o.amount !== undefined ? formatAmount(o.amount) : "" },
      { key: "Close date", value: o.closeDate },
      { key: "Next step", value: o.nextStep },
      { key: "Description", value: o.description },
      { key: "Updates opp", value: ai.existingOpportunityId ? <code>{ai.existingOpportunityId}</code> : "" },
      { key: "Stage change reason", value: ai.stageChangeReason },
    ];
  })();
  // close-outs propose very few "opportunity" fields - show the close-out itself instead
  const closeFields = closeOut
    ? [
        { key: "Opportunity", value: closeTarget ? <ExternalLink href={closeTarget.url}>{closeTarget.name}</ExternalLink> : ai.existingOpportunityId ? <code>{ai.existingOpportunityId}</code> : "" },
        { key: "Outcome", value: closeOut.outcome === "won" ? "Closed Won" : closeOut.outcome === "lost" ? "Closed Lost" : "" },
        { key: "Close date", value: closeOut.closeDate },
        { key: "Amount", value: closeOut.wonAmount !== null && closeOut.wonAmount !== undefined ? formatAmount(closeOut.wonAmount) : "" },
        { key: "# of Curate events", value: closeOut.numberOfEvents !== null && closeOut.numberOfEvents !== undefined ? String(closeOut.numberOfEvents) : "" },
        { key: "Loss reason", value: closeOut.lossReason },
      ]
    : [];

  const canReprocess = !["applied", "applying", "parsing", "interpreting"].includes(doc.status);

  return (
    <>
      <div className="ob-detail-head">
        <div style={{ minWidth: 0 }}>
          <div className="ob-detail-head__title">
            <h1 className="ob-ellipsis" style={{ maxWidth: 720 }} title={doc.email && doc.email.subject}>
              {(doc.email && doc.email.subject) || "(no subject)"}
            </h1>
            <StatusPill status={doc.status} />
          </div>
          <div className="ob-detail-head__meta">
            <Avatar name={doc.forwarder && doc.forwarder.name} email={doc.forwarder && doc.forwarder.email} size={22} />
            <span>
              {isRequest ? "Sent by" : "Forwarded by"} <b style={{ color: "var(--ob-text)", fontWeight: 500 }}>{forwarderName}</b> ({doc.forwarder && doc.forwarder.email})
            </span>
            <span>·</span>
            <span title={doc.receivedAt}>{formatDateTime(doc.receivedAt)}</span>
            {isRequest && (
              <>
                <span>·</span>
                <ModePill mode="request" />
              </>
            )}
            {doc.attempt > 1 && (
              <>
                <span>·</span>
                <span>attempt {doc.attempt}</span>
              </>
            )}
            {doc.source && doc.source !== "sendgrid" && (
              <>
                <span>·</span>
                <span className="ob-pill ob-pill--sm ob-pill--slate">{doc.source}</span>
              </>
            )}
          </div>
        </div>
        <div className="ob-detail-head__actions">
          {result.opportunity && result.opportunity.url && (
            <a className="ob-btn ob-btn--primary" href={result.opportunity.url} target="_blank" rel="noopener noreferrer">
              Open opportunity in Salesforce
            </a>
          )}
          <button type="button" className="ob-btn" disabled={!canReprocess || !!busy} onClick={onReprocess} title={canReprocess ? "Re-run parsing and AI interpretation from the original email" : "Already written to Salesforce"}>
            {busy === "reprocess" ? "Reprocessing…" : "Reprocess"}
          </button>
          <a className="ob-btn ob-btn--ghost" href={rawUrl(doc._id)} target="_blank" rel="noopener noreferrer">
            Download .eml
          </a>
          <Link className="ob-btn ob-btn--ghost" to="/landmark/oppbot">
            Back to inbox
          </Link>
        </div>
      </div>

      {err && <div className="ob-notice ob-notice--danger" style={{ marginBottom: 12 }}>{err}</div>}
      {doc.status === "failed" && doc.error && doc.error.message && (
        <div className="ob-notice ob-notice--danger" style={{ marginBottom: 12 }}>
          Failed at <b>{doc.error.stage}</b>: {doc.error.message}
        </div>
      )}
      {doc.status === "rejected" && (
        <div className="ob-notice ob-notice--danger" style={{ marginBottom: 12 }}>
          {doc.forwarder && doc.forwarder.allowed === false
            ? `Rejected automatically: sender ${doc.forwarder.email} is not on the allowed domain list.`
            : `Rejected by ${(doc.review && (doc.review.decidedByName || doc.review.decidedBy)) || "a reviewer"}${doc.review && doc.review.note ? `: ${doc.review.note}` : "."}`}
        </div>
      )}
      {processing && (
        <div className="ob-notice ob-notice--info" style={{ marginBottom: 12 }}>
          The bot is still working on this email. This page updates automatically.
        </div>
      )}

      <div className="ob-detail-grid">
        {/* ------------------------------------------------ email */}
        <div className="ob-col ob-col--email">
          <Panel
            eyebrow="The email"
            title={isRequest ? "What was asked" : "What was forwarded"}
            actions={
              <button type="button" className="ob-btn ob-btn--ghost ob-btn--sm" onClick={() => setShowRaw((v) => !v)}>
                {showRaw ? "Hide plain text" : "Plain text"}
              </button>
            }
          >
            {parsed.notes ? (
              <div className="ob-notes">
                <div className="ob-notes__label">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
                  </svg>
                  {isRequest ? "Message from" : "Notes from"} {(doc.forwarder && doc.forwarder.name) || "forwarder"}
                </div>
                <div className="ob-notes__text">{parsed.notes}</div>
                {parsed.notesBullets && parsed.notesBullets.length > 0 && (
                  <ul className="ob-notes__bullets">
                    {parsed.notesBullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="ob-notice">{isRequest ? "The message body was empty." : "No notes were added above the forwarded thread."}</div>
            )}
            {(parsed.linkedInUrls || []).length > 0 && (
              <div className="ob-participants">
                {parsed.linkedInUrls.map((u) => (
                  <a key={u} className="ob-participants__chip is-linkedin" href={u} target="_blank" rel="noopener noreferrer" title="LinkedIn profile mentioned in the message">
                    in · {u.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//, "")}
                  </a>
                ))}
              </div>
            )}

            {(parsed.externalParticipants || []).length + (parsed.internalParticipants || []).length > 0 && (
              <div className="ob-participants">
                {(parsed.externalParticipants || []).map((p) => (
                  <span key={p.email} className="ob-participants__chip is-external" title={`${p.email} (external)`}>
                    <Avatar name={p.name} email={p.email} size={18} />
                    {p.name || p.email}
                  </span>
                ))}
                {(parsed.internalParticipants || []).map((p) => (
                  <span key={p.email} className="ob-participants__chip" title={`${p.email} (internal)`}>
                    <Avatar name={p.name} email={p.email} size={18} />
                    {p.name || p.email}
                  </span>
                ))}
              </div>
            )}

            {showRaw ? (
              <pre className="ob-raw">{(doc.email && doc.email.textBody) || "(no plain text body)"}</pre>
            ) : (
              <div className="ob-thread">
                {(parsed.thread || []).length === 0 && !processing && (
                  <div className="ob-muted">{isRequest ? "Sent directly to the bot - there is no forwarded thread. The bot worked from the message above." : "No forwarded messages detected."}</div>
                )}
                {(parsed.thread || []).map((m, i) => (
                  <ThreadMessage key={i} message={m} defaultOpen={i === 0 || m.isExternal} />
                ))}
              </div>
            )}
          </Panel>

          {doc.statusHistory && doc.statusHistory.length > 0 && (
            <Panel eyebrow="Audit trail" title="History">
              <ul className="ob-history">
                {[...doc.statusHistory].reverse().map((h, i) => (
                  <li key={i}>
                    <time title={h.at}>{formatDateTime(h.at)}</time>
                    <span>
                      <StatusPill status={h.status} small /> {h.note ? ` ${h.note}` : ""}
                      {h.by && h.by !== "system" ? <span className="ob-muted"> · {h.by}</span> : ""}
                    </span>
                  </li>
                ))}
              </ul>
              {doc.notify && doc.notify.replySentAt && (
                <div className="ob-muted" style={{ marginTop: 10, fontSize: 12.5 }}>
                  Confirmation email sent to {doc.notify.replyTo} at {formatDateTime(doc.notify.replySentAt)}.
                </div>
              )}
            </Panel>
          )}
        </div>

        {/* ------------------------------------------------ AI */}
        <div className="ob-col">
          <Panel eyebrow="What the AI understood" title="Interpretation">
            {!ai.summary && processing && <Spinner label={doc.status === "interpreting" ? "Reading the email…" : "Waiting for the AI…"} />}
            {!ai.summary && !processing && <div className="ob-muted">No interpretation recorded.</div>}
            {ai.summary && (
              <>
                <p className="ob-summary">{ai.summary}</p>
                <div className="ob-ai-row">
                  <IntentPill doc={doc} />
                  {!isClose && ai.opportunity && ai.opportunity.recordTypeName && <RecordTypePill name={ai.opportunity.recordTypeName} curateCategory={ai.opportunity.curateCategory} />}
                  {isClose && closeTarget && <RecordTypePill name={closeTarget.recordTypeName} curateCategory={closeTarget.curateCategory} />}
                  <ConfidenceBar value={ai.confidence} threshold={threshold} />
                </div>
                {isClose && <CloseOutPanel closeOut={closeOut} target={closeTarget} flags={ai.ambiguityFlags} plan={plan} forwarderName={forwarderName} />}
                {ai.opportunity && (ai.opportunity.recordTypeReason || ai.opportunity.recordTypeName) && ["new_opportunity", "update_opportunity"].includes(ai.intent) && (
                  <div className="ob-routing">
                    <span className="ob-routing__label">Routed to {ai.opportunity.recordTypeName || "—"}</span>
                    <span className="ob-routing__why">{ai.opportunity.recordTypeReason || recordTypeMeta(ai.opportunity.recordTypeName).blurb}</span>
                  </div>
                )}
                {ai.ambiguityFlags && ai.ambiguityFlags.length > 0 && (
                  <div className="ob-flags" style={{ marginTop: 12 }}>
                    {ai.ambiguityFlags.map((f) => (
                      <span key={f} className="ob-flag" title={f}>
                        {FLAG_LABELS[f] || f}
                      </span>
                    ))}
                  </div>
                )}

                <h3>{isRequest ? "How the request was read" : "How the notes were read"}</h3>
                {ai.notesInterpretation && ai.notesInterpretation.length > 0 ? (
                  <ul className="ob-notes-interp">
                    {ai.notesInterpretation.map((n, i) => (
                      <li key={i}>
                        <span className="ob-notes-interp__note">{n.note}</span>
                        <span className="ob-notes-interp__meaning">{n.meaning}</span>
                        {n.fieldsAffected && n.fieldsAffected.length > 0 && (
                          <span className="ob-notes-interp__fields">
                            {n.fieldsAffected.map((f) => (
                              <span key={f}>{f}</span>
                            ))}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="ob-muted">No notes to interpret.</div>
                )}

                <h3>Prospect</h3>
                {ai.prospect && (ai.prospect.companyName || (ai.prospect.people || []).length) ? (
                  <>
                    <div style={{ marginBottom: 8 }}>
                      <b>{ai.prospect.companyName || "Unknown company"}</b>
                      {ai.prospect.domain && <span className="ob-muted"> · {ai.prospect.domain}</span>}
                      {ai.prospect.matchedAccountId ? <span className="ob-pill ob-pill--sm ob-pill--info" style={{ marginLeft: 8 }}>existing account</span> : <span className="ob-pill ob-pill--sm ob-pill--success" style={{ marginLeft: 8 }}>new account</span>}
                    </div>
                    <ul className="ob-people">
                      {(ai.prospect.people || []).map((p, i) => (
                        <li key={i}>
                          <Avatar name={p.name} email={p.email} size={26} />
                          <div className="ob-people__main">
                            <div className="ob-people__name">
                              {p.name}
                              {p.role === "primary" && <span className="ob-muted" style={{ fontSize: 11, marginLeft: 6 }}>primary</span>}
                              {p.linkedInUrl && (
                                <a className="ob-people__li" href={p.linkedInUrl} target="_blank" rel="noopener noreferrer" title={p.linkedInUrl}>
                                  in
                                </a>
                              )}
                            </div>
                            <div className="ob-people__sub">{[p.title, p.email || (p.linkedInUrl ? "no email yet" : "")].filter(Boolean).join(" · ")}</div>
                          </div>
                          {p.matchedContactId ? <span className="ob-pill ob-pill--sm ob-pill--info">existing</span> : <span className="ob-pill ob-pill--sm ob-pill--success">new</span>}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <div className="ob-muted">No prospect identified.</div>
                )}
                {linkedInProfiles.length > 0 && (
                  <div className="ob-li-cards">
                    {linkedInProfiles.map((p) => (
                      <LinkedInProfileCard key={p.url || p.slug} profile={p} />
                    ))}
                  </div>
                )}

                <h3>{isClose ? "Proposed close-out" : "Proposed opportunity"}</h3>
                <KeyValueList items={isClose ? closeFields : oppFields} />

                <details className="ob-reasoning">
                  <summary>Reasoning</summary>
                  <p>{ai.reasoning || "—"}</p>
                </details>
                <div className="ob-ai-meta">
                  <span>{ai.model}</span>
                  <span>prompt v{ai.promptVersion}</span>
                  {ai.durationMs ? <span>{formatMs(ai.durationMs)}</span> : null}
                  {doc.timings && doc.timings.total ? <span>total {formatMs(doc.timings.total)}</span> : null}
                </div>
              </>
            )}
          </Panel>

          {doc.context && ((doc.context.accounts || []).length > 0 || (doc.context.openOpportunities || []).length > 0) && (
            <Panel eyebrow="Salesforce context" title="What already existed">
              {(doc.context.accounts || []).length > 0 && (
                <>
                  <div className="ob-eyebrow" style={{ marginTop: 4 }}>Matching accounts</div>
                  <ul className="ob-people">
                    {doc.context.accounts.map((a) => (
                      <li key={a.sfId}>
                        <div className="ob-people__main">
                          <div className="ob-people__name">{a.name}</div>
                          <div className="ob-people__sub">{[a.website, a.matchedDomain ? `matched ${a.matchedDomain}` : "", a.viaContact ? `via ${a.viaContact}` : "", a.viaLinkedIn ? `via LinkedIn${a.matchedBy ? ` (${a.matchedBy})` : ""}` : ""].filter(Boolean).join(" · ")}</div>
                        </div>
                        <ExternalLink href={a.url}>Open</ExternalLink>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {(doc.context.openOpportunities || []).length > 0 && (
                <>
                  <div className="ob-eyebrow" style={{ marginTop: 12 }}>Opportunities on those accounts</div>
                  <ul className="ob-people">
                    {doc.context.openOpportunities.map((o) => (
                      <li key={o.sfId} style={ai.existingOpportunityId === o.sfId ? { borderColor: "var(--ob-info)", background: "var(--ob-info-soft)" } : undefined}>
                        <div className="ob-people__main">
                          <div className="ob-people__name">
                            {o.name}
                            {o.isClosed && <span className="ob-muted" style={{ fontSize: 11, marginLeft: 6 }}>{o.isWon ? "closed won" : "closed lost"}</span>}
                            {ai.existingOpportunityId === o.sfId && <span className="ob-muted" style={{ fontSize: 11, marginLeft: 6 }}>{isClose ? "target of the close-out" : "target of the update"}</span>}
                          </div>
                          <div className="ob-people__sub">
                            {[o.recordTypeName, o.curateCategory ? curateLabel(o.curateCategory) : "", o.stageName, o.ownerName, o.summitEventName, o.closeDate, o.amount !== null && o.amount !== undefined ? formatAmount(o.amount) : ""].filter(Boolean).join(" · ")}
                          </div>
                          {o.lossReason && <div className="ob-people__sub ob-people__sub--quote">Loss reason on file: {o.lossReason}</div>}
                        </div>
                        <ExternalLink href={o.url}>Open</ExternalLink>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Panel>
          )}
        </div>

        {/* ------------------------------------------------ Salesforce */}
        <div className="ob-col">
          <Panel
            eyebrow="Salesforce"
            title={doc.status === "applied" ? "What was written" : doc.status === "needs_review" ? "Proposed actions" : "Planned actions"}
            tone={doc.status === "needs_review" ? "warning" : doc.status === "applied" ? "success" : doc.status === "failed" ? "danger" : undefined}
          >
            {result.opportunity && result.opportunity.sfId && (
              <div className="ob-result-card">
                <div className="ob-result-card__top">
                  <div>
                    <div className="ob-eyebrow">
                      {result.opportunity.extra && result.opportunity.extra.alreadyClosed
                        ? "Opportunity already closed - email logged"
                        : result.opportunity.extra && result.opportunity.extra.closed
                        ? `Opportunity closed ${result.opportunity.extra.closed === "won" ? "won" : "lost"}`
                        : result.opportunity.extra && result.opportunity.extra.updated
                        ? "Opportunity updated"
                        : "Opportunity created"}
                    </div>
                    <div className="ob-result-card__name">{result.opportunity.name}</div>
                  </div>
                  <ExternalLink href={result.opportunity.url}>Open</ExternalLink>
                </div>
                <div className="ob-result-card__meta">
                  {result.opportunity.extra && result.opportunity.extra.stageName && (
                    <span>
                      <b>Stage</b>
                      {result.opportunity.extra.stageName}
                    </span>
                  )}
                  {result.opportunity.extra && result.opportunity.extra.ownerName && (
                    <span>
                      <b>Owner</b>
                      {result.opportunity.extra.ownerName}
                    </span>
                  )}
                  {result.opportunity.extra && result.opportunity.extra.closeDate && (
                    <span>
                      <b>Close</b>
                      {result.opportunity.extra.closeDate}
                    </span>
                  )}
                  {result.account && result.account.name && (
                    <span>
                      <b>Account</b>
                      {result.account.name}
                    </span>
                  )}
                  {result.opportunity.extra && result.opportunity.extra.amount !== null && result.opportunity.extra.amount !== undefined && result.opportunity.extra.closed && (
                    <span>
                      <b>Amount</b>
                      {formatAmount(result.opportunity.extra.amount)}
                    </span>
                  )}
                  {result.opportunity.extra && result.opportunity.extra.changedFields && result.opportunity.extra.changedFields.length > 0 && (
                    <span>
                      <b>Changed</b>
                      {result.opportunity.extra.changedFields.join(", ")}
                    </span>
                  )}
                </div>
                {result.opportunity.extra && result.opportunity.extra.lossReason && <div className="ob-result-card__quote">{result.opportunity.extra.lossReason}</div>}
                <div className="ob-result-card__links">
                  {result.account && result.account.url && <ExternalLink href={result.account.url}>{result.account.created ? "New account" : "Account"}</ExternalLink>}
                  {(result.contacts || []).map((c) => (
                    <ExternalLink key={c.sfId} href={c.url}>
                      {c.created ? "New contact: " : "Contact: "}
                      {c.name}
                    </ExternalLink>
                  ))}
                  {result.task && result.task.url && <ExternalLink href={result.task.url}>Logged task</ExternalLink>}
                </div>
              </div>
            )}

            {doc.status === "not_sales" && <div className="ob-notice" style={{ marginBottom: 12 }}>The AI decided this email is not a sales opportunity, so nothing was written to Salesforce.</div>}
            {plan.gateReason && doc.status === "applied" && plan.autoApply && (
              <div className="ob-notice ob-notice--success" style={{ marginBottom: 12 }}>
                Applied automatically: {plan.gateReason}.
              </div>
            )}
            {doc.review && doc.review.decision === "approved" && (
              <div className="ob-notice ob-notice--success" style={{ marginBottom: 12 }}>
                Approved by {doc.review.decidedByName || doc.review.decidedBy}
                {doc.review.edited ? " with edits" : ""}
                {doc.review.note ? ` · "${doc.review.note}"` : ""}.
              </div>
            )}

            {processing && !plan.actions?.length ? <Spinner label="Planning…" /> : <ActionTimeline actions={plan.actions} meta={meta} />}

            {doc.status === "needs_review" && <ReviewForm doc={doc} meta={meta} onDone={refetch} />}
          </Panel>

          {plan.actions && plan.actions.length > 0 && doc.status !== "needs_review" && (
            <Panel eyebrow="Fields" title="Opportunity field values">
              <KeyValueList
                items={Object.entries((plan.actions.find((a) => ["create_opportunity", "update_opportunity", "close_opportunity"].includes(a.type)) || {}).fields || {}).map(([k, v]) => ({
                  key: k,
                  value: describeValue(k, v, meta),
                }))}
              />
            </Panel>
          )}
        </div>
      </div>
    </>
  );
}
